// src/services/patient.js

// Base API URL - should match other services
const API_BASE_URL = `${import.meta.env.VITE_API_URL}/api`;
const API_KEY = import.meta.env.VITE_API_KEY


/**
 * Fetch the current user's patient profile
 * @param {string} token - JWT authentication token
 * @returns {Promise<Object|null>} - Patient profile object or null if not found
 */
export async function fetchMyPatientProfile(token) {
  try {
    const response = await fetch(`${API_BASE_URL}/patients/protected/me`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'X-API-Key': API_KEY
      }
    });
    
    if (response.status === 404) {
      return null; // Patient profile not found
    }
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching patient profile:', error);
    throw error;
  }
}

/**
 * Create a new patient profile for the current user
 * @param {string} token - JWT authentication token
 * @param {Object} patientData - Patient profile data
 * @returns {Promise<Object>} - Created patient profile
 */
export async function createPatientProfile(token, patientData) {
  const response = await fetch(`${API_BASE_URL}/patients/protected`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY
    },
    body: JSON.stringify(patientData)
  });
  
  if (!response.ok) {
    if (response.status === 400) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create patient profile');
    }
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return await response.json();
}

/**
 * Update the current user's patient profile
 * @param {string} token - JWT authentication token
 * @param {Object} patientData - Updated patient profile data
 * @returns {Promise<Object>} - Updated patient profile
 */
export async function updatePatientProfile(token, patientData) {
  const response = await fetch(`${API_BASE_URL}/patients/protected/me`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY
    },
    body: JSON.stringify(patientData)
  });
  
  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Patient profile not found');
    } else if (response.status === 400) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Invalid data');
    }
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return await response.json();
}

/**
 * Check if the current user has a patient profile
 * @param {string} token - JWT authentication token
 * @returns {Promise<boolean>} - True if user has a patient profile
 */
export async function hasPatientProfile(token) {
  try {
    const profile = await fetchMyPatientProfile(token);
    return profile !== null;
  } catch (error) {
    console.error('Error checking patient profile:', error);
    return false;
  }
}

/**
 * For admin use only - fetch a specific patient profile by ID
 * @param {string} token - JWT authentication token
 * @param {string} patientId - ID of the patient to fetch
 * @returns {Promise<Object>} - Patient profile
 */
export async function fetchPatientById(token, patientId) {
  const response = await fetch(`${API_BASE_URL}/patients/admin?id=${patientId}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY
    }
  });
  
  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Patient not found');
    } else if (response.status === 401) {
      throw new Error('Unauthorized to access this patient');
    }
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return await response.json();
}

/**
 * For admin use only - update a specific patient profile
 * @param {string} token - JWT authentication token
 * @param {string} patientId - ID of the patient to update
 * @param {Object} patientData - Updated patient data
 * @returns {Promise<Object>} - Updated patient profile
 */
export async function updatePatientById(token, patientId, patientData) {
  const response = await fetch(`${API_BASE_URL}/patients/admin/${patientId}`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY
    },
    body: JSON.stringify(patientData)
  });
  
  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Patient not found');
    } else if (response.status === 401) {
      throw new Error('Unauthorized to update this patient');
    }
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return await response.json();
}

/**
 * For admin use only - delete a patient profile
 * @param {string} token - JWT authentication token
 * @param {string} patientId - ID of the patient to delete
 * @returns {Promise<void>}
 */
export async function deletePatient(token, patientId) {
  const response = await fetch(`${API_BASE_URL}/patients/admin/${patientId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY
    }
  });
  
  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Patient not found');
    } else if (response.status === 401) {
      throw new Error('Unauthorized to delete this patient');
    }
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  // DELETE typically returns no content
  return;
}

/**
 * Upload a patient profile image
 * @param {string} token - JWT authentication token
 * @param {File} imageFile - The image file to upload
 * @returns {Promise<Object>} - Response with image URL info or error details
 */
export async function uploadPatientProfileImage(token, imageFile) {
  // Create a FormData object to send the file
  const formData = new FormData();
  formData.append('file', imageFile);
  
  try {
    // Use the correct API path
    const apiUrl = `${API_BASE_URL}/patients/protected/profile-image`;    
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-API-Key': API_KEY
      },
      body: formData
    });
        
    if (!response.ok) {
      let errorMessage = `HTTP error! Status: ${response.status}`;
      
      try {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } else {
          const textResponse = await response.text();
          if (textResponse) {
            errorMessage = `${errorMessage}: ${textResponse}`;
          }
        }
      } catch (parseError) {
        console.error('Error parsing error response:', parseError);
      }
      
      // Return an object with error info instead of throwing
      return {
        success: false,
        error: errorMessage,
        serverIssue: true
      };
    }
    
    // Handle successful response
    try {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const data = await response.json();
        return {
          success: true,
          image_url: data.presigned_url || data.profile_image
        };
      } else {
        const textResponse = await response.text();
        return { 
          success: true,
          message: 'Image uploaded successfully', 
          text_response: textResponse 
        };
      }
    } catch (parseError) {
      console.error('Error parsing success response:', parseError);
      return {
        success: false,
        error: 'Could not parse server response',
        serverIssue: true
      };
    }
  } catch (error) {
    console.error('Error uploading profile image:', error);
    return {
      success: false,
      error: error.message,
      serverIssue: true
    };
  }
}

/**
 * Get the patient's profile image URL
 * @param {string} token - JWT authentication token
 * @returns {Promise<Object>} - URL of the profile image or error details
 */
export async function getPatientProfileImage(token) {
  try {
    const apiUrl = `${API_BASE_URL}/patients/protected/me/profile-image`;
    
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'X-API-Key': API_KEY
      }
    });
        
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    
    const data = await response.json();
    
    return {
      success: true,
      image_url: data.presigned_url || null
    };
  } catch (error) {
    console.error('Error in getPatientProfileImage:', error);
    return {
      success: false,
      error: error.message,
      serverIssue: true
    };
  }
}

/**
 * Recover patient password using security answers
 * @param {string} token - JWT authentication token
 * @param {Object} securityAnswers - Security answers object
 * @returns {Promise<Object>} - Recovery response with password
 */
export async function recoverPatientPassword(token) {
  const response = await fetch(`${API_BASE_URL}/patients/protected/me/recover-password`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY
    },
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return await response.json();
}